import axios, { AxiosInstance, AxiosResponse } from "axios";
import { showLoader, hideLoader, refreshToken } from "./msal-utils"; //checkTokenValidity, refreshToken, 

const defaultHeaders = {
  "Content-Type": "application/json",
};
const getAccessToken = () => {
  // PingOne migration: send the IdToken (not AccessToken) as the Authorization header.
  // PingOne access tokens contain only authorization context (scope, sub, aud, etc.)
  // and lack the identity claims (email, fname, lname, etc.) that the .NET API's
  // TokenDetails.GetTokenDetails reads. The IdToken carries those claims and is signed
  // by the same JWKS, so the JwtBearer middleware accepts it.
  //
  // Non-SSO (legacy username/password) flow still uses AccessToken-NonSSO.
  const idToken = localStorage.getItem("IdToken");
  if (idToken) return "Bearer " + idToken;

  const SSOToken = localStorage.getItem("AccessToken");
  const nonSSOToken = localStorage.getItem("AccessToken-NonSSO");
  return SSOToken ?? nonSSOToken;
};

class httpConfig {
  Axios: AxiosInstance;
  constructor(baseURL: string) {
    this.Axios = axios.create({
      baseURL,
    });
    this.setUpInterceptors();
  }

  setUpInterceptors() {
    this.Axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: any) => {
        const errorData = error?.response?.data || error;
        const errorObj = {
          ...errorData,
          message: errorData?.message?.includes("Already Exists")
            ? errorData?.message
              : "Operation failed. Please try again after some time.",
        };

        if (errorData.correlationId) {
          errorObj.message = `${errorObj.message}\nCode: ${errorData.correlationId}`;
        }

        const errCode = error?.response?.status;
        if (
          errCode === 404 ||
          errCode === 500 ||
          errCode === 400 ||
          errCode === 504
        ) {
          //store.dispatch(hideLoadingSpinner());
          //store.dispatch(setErrorNotification(errorObj));
        }
        if (typeof error?.response === "undefined") {
          // Cross-Origin Request Blocked
          const corsErr = {
            type: "CORB",
            message: "Operation failed. Please try again after some time",
          };
          errorData.push(corsErr);
        }
        throw error;
      }
    );

    this.Axios.interceptors.request.use(
      async (request: any) => {
        //await checkTokenValidity();
        await refreshToken();
        request.headers.Authorization = getAccessToken();
        return request;
      },
      (error: any) => {
        throw error;
      }
    );
  }

  getRequest(url: string, headers: any = {}, config: any = {}) {
    showLoader();
    // `config` is spread into the axios request options so callers can pass
    // e.g. an AbortController `signal` without affecting existing call sites.
    return this.Axios.get(url, {
      headers: { ...defaultHeaders, ...headers },
      ...config,
    }).then(function (response) {
      hideLoader();
      return (response?.data ? response.data : response);
    }).catch(function (error) { hideLoader(); throw error });
  }

  // Binary/file download. Returns the full AxiosResponse so callers can read the Blob (response.data)
  // and Content-Disposition (response.headers) for the filename.
  getBlobRequest(url: string, headers: any = {}) {
    showLoader();
    return this.Axios.get(url, {
      responseType: "blob",
      headers: { ...headers },
    }).then(function (response) {
      hideLoader();
      return response;
    }).catch(function (error) { hideLoader(); throw error });
  }

  postRequest(url: string, data: any, headers: any = {}) {
    showLoader();
    return this.Axios.post(url, data, {
      headers: { ...defaultHeaders, ...headers },
    }).then(function (response) {
      hideLoader();
      return (response?.data ? response.data : response);
    }).catch(function (error) { hideLoader(); throw error });
  }

  postStringRequest(url: string, data: any, headers: any = {}) {
    showLoader();
    let req = JSON.stringify(data).toString();
    const replaceStr = /"/gi;
    req = req.replace(replaceStr, '\\"');
    return this.Axios.post(url, '"' + req + '"', {
      headers: { ...defaultHeaders, ...headers },
    }).then(function (response) {
      hideLoader();
      return (response?.data ? response.data : response);
    }).catch(function (error) { hideLoader(); throw error });
  }

    // added to remove loader for background processes
    postStrReqNoLoader(url: string, data: any, headers: any = {}) {
    
      let req = JSON.stringify(data).toString();
      const replaceStr = /"/gi;
      req = req.replace(replaceStr, '\\"');
      return this.Axios.post(url, '"' + req + '"', {
        headers: { ...defaultHeaders, ...headers },
      }).then(function (response) {
        
        return (response?.data ? response.data : response);
      }).catch(function (error) {  throw error });
    }
  
  deleteRequest = (url: string, headers: any = {}) =>
    this.Axios.delete(url, {
      headers: { ...defaultHeaders, ...headers },
    }).then((response) => (response?.data ? response.data : response));

  deleteRequestWithPayload = (url: string, data?: any, headers: any = {}) =>
    this.Axios.delete(url, {
      data,
      headers: { ...defaultHeaders, ...headers },
    }).then((response) => (response?.data ? response.data : response));

  putRequest = (url: string, data: any = {}, headers: any = {}) =>
    this.Axios.put(url, data, {
      headers: { ...defaultHeaders, ...headers },
    }).then((response) => (response?.data ? response.data : response));

  patchRequest(url: string, data: any, headers: any = {}) {
    return this.Axios.patch(url, data, {
      headers: { ...defaultHeaders, ...headers },
    }).then((response) => (response?.data ? response.data : response));
  }
}

export default httpConfig;
